import React from "react";
import {useTranslation} from "@unibuddy/intl";
import {Form, FormGroup, Stack, SubmitButton, TextInput} from "@unibuddy/patron";
import {string} from "yup";
import {ErrorMessage, FormikHelpers} from "formik";

import {PASSWORD_MIN_CHAR} from "shared/Shared/validations/validationMessages";

import {
    INTL_NEW_PASSWORD,
    INTL_CHANGE_PASSWORD,
    INTL_RESET_LINK_EXPIRED,
} from "../../../../translations/passwordReset";
import {IntlNamespaces} from "../../../../translations/types";
import {INTL_PASSWORD_REQUIRED, INTL_PASSWORD_MIN_CHAT} from "../../../../translations/validations";
import {PASSWORD_MINIMUM_LENGTH} from "../../../../widgets/modules/RegisterNew/components/RegisterPage1Form";
import {AuthFormError} from "../../../../components/AuthFormError/AuthFormError";

type FormValues = {
    password: string;
};

type ResetPasswordFormProps = {
    handleSubmit: (
        password: string,
        setFieldError: (field: string, message: string | undefined) => void,
    ) => Promise<void>;
};

export function ResetPasswordForm({handleSubmit}: ResetPasswordFormProps) {
    const {t} = useTranslation();

    return (
        <Form
            onSubmit={async (
                values: {
                    password: string;
                },
                {setSubmitting, setFieldError}: FormikHelpers<FormValues>,
            ) => {
                setSubmitting(true);
                try {
                    await handleSubmit(values.password, setFieldError);
                } catch (error) {
                    setFieldError(
                        "serverError",
                        t(
                            `${IntlNamespaces.PASSWORD_RESET}:${INTL_RESET_LINK_EXPIRED.key}`,
                            INTL_RESET_LINK_EXPIRED.defaultValue,
                        ),
                    );
                } finally {
                    setSubmitting(false);
                }
            }}
            validationSchema={{
                password: string()
                    .required(
                        t(
                            `${IntlNamespaces.VALIDATIONS}:${INTL_PASSWORD_REQUIRED.key}`,
                            INTL_PASSWORD_REQUIRED.defaultValue,
                        ),
                    )
                    .min(
                        PASSWORD_MINIMUM_LENGTH,
                        t(
                            `${IntlNamespaces.VALIDATIONS}:${INTL_PASSWORD_MIN_CHAT.key}`,
                            PASSWORD_MIN_CHAR(PASSWORD_MINIMUM_LENGTH),
                            {
                                minChar: PASSWORD_MINIMUM_LENGTH,
                            },
                        ),
                    ),
            }}
        >
            {(props: {errors: {serverError: string}}) => {
                const {errors} = props;
                return (
                    <Stack space="medium-large">
                        <FormGroup
                            id="password"
                            label={t(INTL_NEW_PASSWORD.key, INTL_NEW_PASSWORD.defaultValue)}
                        >
                            <TextInput name="password" type="password" required />
                        </FormGroup>
                        {errors.serverError && (
                            <ErrorMessage name="serverError" component={AuthFormError} />
                        )}
                        <SubmitButton id="continue" color="primary" block>
                            {t(INTL_CHANGE_PASSWORD.key, INTL_CHANGE_PASSWORD.defaultValue)}
                        </SubmitButton>
                    </Stack>
                );
            }}
        </Form>
    );
}
